/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import PathBrowserify from 'path-browserify';

import FileSystemPath from './file-system-path';

/**
 * @import { FileSystemName } from '.'
 */

/**
 * Továrna na cesty v souborovém systému.
 */
export default class FileSystemPathFactory {
  /**
   * Vytvoří cestu z informací o jejích částech.
   * @param {Object} [opts={}] Nepovinné parametry.
   * @param {FileSystemName} [opts.base=''] Název záznamu.
   * @param {FileSystemName} [opts.extension=''] Přípona názvu záznamu.
   * @param {FileSystemName} [opts.unit=''] Název jednotky.
   * @param {FileSystemName[]} [opts.parents=[]] Seznam názvů rodičovských složek.
   * @param {FileSystemName} [opts.stem=''] Část názvu záznamu bez přípony.
   * @returns {FileSystemPath}
   */
  static fromInfo({
    base = '',
    extension = '',
    unit = '',
    parents = [],
    stem = ''
  } = {}) {
    const segmentArray = [];
    if (!isString(unit)) {
      throw new TypeError(`Invalid unit "${unit}".`);
    }
    if (unit) {
      segmentArray.push(`/${unit}`);
    }
    if (!isArray(parents)) {
      throw new TypeError(`Invalid parents "${parents}".`);
    }
    parents.forEach((parent) => {
      if (!isString(parent)) {
        throw new TypeError(`Invalid parent "${parent}".`);
      }
      segmentArray.push(parent);
    });
    if (!isString(base)) {
      throw new TypeError(`Invalid base "${base}".`);
    }
    if (base) {
      segmentArray.push(base);
    } else {
      const altBase = FileSystemPathFactory.getBase({ extension, stem });
      if (altBase) {
        segmentArray.push(altBase);
      }
    }
    const value = segmentArray.join('/');
    return new FileSystemPath(value);
  }

  /**
   * Vytvoří cestu z její hodnoty reprezentované řetězcem.
   * @param {string} value Řetězec s cestou.
   * @returns {FileSystemPath}
   */
  static fromValue(value) {
    // TODO Přejmenovat na fromString
    // - Aby odpovídalo změně FileSystemPath#toValue na #toString
    return new FileSystemPath(value);
  }

  /**
   * Zjistí název záznamu z jeho podčástí.
   * @param {Object} [opts={}] Nepovinné parametry.
   * @param {FileSystemName} [opts.extension=''] Přípona názvu záznamu.
   * @param {FileSystemName} [opts.stem=''] Část názvu záznamu bez přípony.
   * @private
   * @returns {FileSystemName} Název záznamu.
   */
  static getBase({ extension = '', stem = '' } = {}) {
    const segmentArray = [];
    if (!isString(stem)) {
      throw new TypeError(`Invalid stem "${stem}".`);
    }
    if (stem) {
      segmentArray.push(stem);
    }
    if (!isString(extension)) {
      throw new TypeError(`Invalid extension "${extension}".`);
    }
    if (extension) {
      segmentArray.push(`.${extension}`);
    }
    return segmentArray.join('');
  }

  /**
   * Spojí cesty do jedné výsledné cesty.
   * @param {...FileSystemPath} pathArray Seznam cest ke spojení.
   * @returns {FileSystemPath} Spojená a normalizovaná cesta.
   */
  static join(...pathArray) {
    const valueArray = pathArray.map((path) => path.getValue());
    const value = PathBrowserify.join(...valueArray);
    return new FileSystemPath(value);
  }

  /**
   * Zjistí relativní cestu mezi dvěmi absolutními.
   * @param {FileSystemPath} fromPath Výchozí cesta.
   * @param {FileSystemPath} toPath Cílová cesta.
   * @returns {FileSystemPath} Relativní cesta.
   */
  static relative(fromPath, toPath) {
    const value = PathBrowserify.relative(
      fromPath.getValue(),
      toPath.getValue()
    );
    return new FileSystemPath(value);
  }

  /**
   * Spojí cesty do jedné absolutní cesty.
   * @param {...FileSystemPath} pathArray Seznam cest ke spojení.
   * @returns {FileSystemPath} Spojená a normalizovaná cesta.
   */
  static resolve(...pathArray) {
    const valueArray = pathArray.map((path) => path.getValue());
    const value = PathBrowserify.resolve(...valueArray);
    return new FileSystemPath(value);
  }
}
